/** Campanhas de automação por palavra-chave (WhatsApp / Instagram). */

export const CAMPAIGN_CHANNELS = [
  "whatsapp",
  "instagram_dm",
  "instagram_story",
  "instagram_comment",
] as const;

export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

const KEYWORD_TRIGGER_TYPES = ["keyword", "story_reply", "comment"] as const;

export function isCampaignChannel(channel: string): channel is CampaignChannel {
  return (CAMPAIGN_CHANNELS as readonly string[]).includes(channel);
}

export function triggerTypeSupportsCampaignKeywords(triggerType: string): boolean {
  return (KEYWORD_TRIGGER_TYPES as readonly string[]).includes(triggerType);
}

export function parseKeywordList(raw: string): string[] {
  return raw.split(",").map((k) => k.trim()).filter(Boolean);
}

export function canRegisterAsCampaign(
  channel: string,
  triggerType: string,
  keywords: string[],
): boolean {
  return (
    isCampaignChannel(channel) &&
    triggerTypeSupportsCampaignKeywords(triggerType) &&
    keywords.length > 0
  );
}

export const CAMPAIGN_CHANNEL_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram_dm: "Instagram DM",
  instagram_story: "Instagram Story",
  instagram_comment: "Instagram Comentário",
};

/** Origens de tráfego para rastreamento UTM (tráfego pago e orgânico). */
export const TRAFFIC_SOURCES = [
  { value: "google_ads", label: "Google Ads", utmMedium: "cpc" },
  { value: "facebook_ads", label: "Facebook / Meta Ads", utmMedium: "cpc" },
  { value: "instagram_ads", label: "Instagram Ads", utmMedium: "cpc" },
  { value: "tiktok_ads", label: "TikTok Ads", utmMedium: "cpc" },
  { value: "linkedin_ads", label: "LinkedIn Ads", utmMedium: "cpc" },
  { value: "youtube_ads", label: "YouTube Ads", utmMedium: "cpc" },
  { value: "twitter_ads", label: "X / Twitter Ads", utmMedium: "cpc" },
  { value: "pinterest_ads", label: "Pinterest Ads", utmMedium: "cpc" },
  { value: "snapchat_ads", label: "Snapchat Ads", utmMedium: "cpc" },
  { value: "site", label: "Site / Landing page", utmMedium: "organic" },
  { value: "email", label: "E-mail marketing", utmMedium: "email" },
  { value: "referral", label: "Indicação / Parceiro", utmMedium: "referral" },
  { value: "other", label: "Outro", utmMedium: "other" },
] as const;

export type TrafficSource = (typeof TRAFFIC_SOURCES)[number]["value"];

export const DEFAULT_TRIGGER_BY_CAMPAIGN_CHANNEL: Record<CampaignChannel, string> = {
  whatsapp: "keyword",
  instagram_dm: "keyword",
  instagram_story: "story_reply",
  instagram_comment: "comment",
};

export function trafficSourceByValue(value: string) {
  return TRAFFIC_SOURCES.find((s) => s.value === value);
}

export const WHATSAPP_INTEGRATION_PROVIDERS = [
  "whatsapp_evolution",
  "whatsapp_cloud",
  "whatsapp_uazapi",
] as const;

export function integrationProvidersForCampaignChannel(
  channel: CampaignChannel,
): readonly string[] {
  if (channel === "whatsapp") return WHATSAPP_INTEGRATION_PROVIDERS;
  return ["instagram"];
}

export type CampaignIntegrationOption = {
  id: string;
  provider: string;
  display_name: string;
  detail?: string;
};

export interface CampaignMetrics {
  triggers: number;
  matched: number;
  failed: number;
  uniqueContacts: number;
  messagesSent: number;
  aiHandoffs: number;
  matchRate: number;
}

export function automationStatusToCampaignStatus(
  automationStatus: string,
): "draft" | "active" | "paused" | "ended" {
  switch (automationStatus) {
    case "active":
      return "active";
    case "paused":
      return "paused";
    case "archived":
      return "ended";
    default:
      return "draft";
  }
}

export async function syncCampaignStatusForAutomation(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  automationId: string,
  automationStatus: string,
): Promise<void> {
  const status = automationStatusToCampaignStatus(automationStatus);
  await supabaseClient
    .from("automation_campaigns")
    .update({ status })
    .eq("automation_id", automationId);
}

export async function setAutomationAndCampaignStatus(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  automationId: string,
  newStatus: string,
): Promise<{ error?: string }> {
  if (newStatus === "active") {
    const { data: row, error: fetchError } = await supabaseClient
      .from("automations")
      .select("compiled_flow, trigger_config")
      .eq("id", automationId)
      .single();

    if (fetchError) return { error: fetchError.message };

    const flow = (row as { compiled_flow?: { nodes?: unknown[] } | null } | null)?.compiled_flow;
    if (!flow?.nodes?.length) {
      return { error: "Salve o fluxo no editor antes de ativar." };
    }

    const { data: campaign } = await supabaseClient
      .from("automation_campaigns")
      .select("integration_id, keywords")
      .eq("automation_id", automationId)
      .maybeSingle();

    if (campaign) {
      const c = campaign as { integration_id?: string | null; keywords?: string[] };
      const existingConfig = ((row as { trigger_config?: Record<string, unknown> }).trigger_config ??
        {}) as Record<string, unknown>;
      const triggerConfig = {
        ...existingConfig,
        keywords: c.keywords ?? existingConfig.keywords ?? [],
        match_mode: existingConfig.match_mode ?? "contains",
        ...(c.integration_id ? { integration_id: c.integration_id } : {}),
      };

      const compiledFlow = flow as {
        nodes: Array<{ id: string; type: string; config: Record<string, unknown> }>;
        edges: unknown[];
      };
      const syncedNodes = compiledFlow.nodes.map((node) =>
        node.type === "trigger"
          ? { ...node, config: { ...node.config, ...triggerConfig } }
          : node,
      );

      const { error: syncError } = await supabaseClient
        .from("automations")
        .update({
          status: newStatus,
          trigger_config: triggerConfig,
          compiled_flow: { ...compiledFlow, nodes: syncedNodes },
        })
        .eq("id", automationId);

      if (syncError) return { error: syncError.message };
      await syncCampaignStatusForAutomation(supabaseClient, automationId, newStatus).catch(() => {});
      return {};
    }
  }

  const { error } = await supabaseClient
    .from("automations")
    .update({ status: newStatus })
    .eq("id", automationId);

  if (error) return { error: error.message };

  await syncCampaignStatusForAutomation(supabaseClient, automationId, newStatus).catch(() => {});
  return {};
}

export function aggregateCampaignMetrics(
  automationId: string,
  logs: Array<{ id: string; automation_id: string; final_status: string; conversation_id: string }>,
  steps: Array<{ execution_log_id: string; action_type: string; status: string }>,
  handoffs: number,
): CampaignMetrics {
  const autoLogs = logs.filter((l) => l.automation_id === automationId);
  const triggers = autoLogs.length;
  const matched = autoLogs.filter((l) => l.final_status === "matched").length;
  const failed = autoLogs.filter((l) => l.final_status === "failed").length;
  const uniqueContacts = new Set(autoLogs.map((l) => l.conversation_id)).size;

  const autoLogIds = new Set(autoLogs.map((l) => l.id));
  const messagesSent = steps.filter(
    (s) =>
      s.action_type === "send_message" &&
      s.status === "completed" &&
      autoLogIds.has(s.execution_log_id),
  ).length;

  const matchRate = triggers > 0 ? Math.round((matched / triggers) * 100) : 0;

  return {
    triggers,
    matched,
    failed,
    uniqueContacts,
    messagesSent,
    aiHandoffs: handoffs,
    matchRate,
  };
}
